// PROD-3310 billing bridge.
// Mirrors: dev-scrips-pm-react src/shared/ui/components/page-header.tsx (`PageHeader`: title, meta, tag, actions,
// tabs, leading, back, className; and `PageTag`).
// Wraps:   the PA's own `PageHeader` (src/shared/ui/components/page-header.tsx), which already renders the same Z4
// header contract as PM's ("the same element as the practitioner app's ContextHeader": 80 tall, 16 padding, title
// 24/32/600 over a 13/20 subline, actions on the subline's bottom line, the inset hairline) — its look lives in
// src/styles/showcase-bridge.css. There is no Signal DS page header yet (graduation candidate).
// Overrides: PM's `back` control has no slot on the PA header; it is drawn in the `leading` slot when no leading photo
// is given (PM puts it on the title's line). PM's `className` is not forwarded: the only one PM's billing page passes
// is `px-[var(--page-pad)]`, which the bridge takes as `inset="page"` (48 each side: the title
// sits at x=48 and the actions end 48 from the right). The shared PA header owns its own 16 padding and is used by other screens, so the bridge wraps it in `data-bridge="page-header"`
// and `src/styles/showcase-billing.css` widens the inset for the billing screens only. Both are PROD-3310 gaps.
import type { ReactNode } from 'react';
import { PageHeader as PaPageHeader } from '@/shared/ui/components/page-header';

export interface PageHeaderProps {
  title: ReactNode;
  meta?: ReactNode;
  tag?: ReactNode;
  actions?: ReactNode;
  tabs?: ReactNode;
  leading?: ReactNode;
  back?: ReactNode;
  /**
   * PM's `className="px-[var(--page-pad)]"` (the arbitrary class the PA forbids) as a prop: `page` widens the header's
   * side inset from 16 to the 48 page pad. Absent, the shared 16 inset stands. PM's other `className` use is not forwarded.
   */
  inset?: 'page';
}

export function PageHeader({ title, meta, tag, actions, tabs, leading, back, inset }: PageHeaderProps) {
  return (
    <div data-bridge="page-header" data-bridge-inset={inset}>
      <PaPageHeader
        title={title}
        meta={meta === '' ? undefined : meta}
        tag={tag}
        actions={actions}
        tabs={tabs}
        leading={leading ?? back}
      />
    </div>
  );
}

/** A quiet tag beside a page title: hairline outline, secondary ink, no fill. */
export function PageTag({ children }: { children: ReactNode }) {
  return (
    <span className="self-center rounded-tag border border-border-default px-2 text-xs font-medium text-text-secondary">
      {children}
    </span>
  );
}
